import type { BillResponse } from '@palaccount/contracts';
import { Banknote, CheckCheck, Pencil, RotateCcw, Scale, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { api } from '@/api';
import { Amount } from '@/components/amount';
import {
  BulkExportButton,
  BulkPdfButton,
  BulkPostButton,
  DocumentListPage,
  DocumentPdfButton,
  documentLocale,
  normalisePaged,
  type DocumentListConfig,
} from '@/components/document-list';
import { Gated } from '@/components/permission-gate';
import { ReasonBox } from '@/components/reason-box';
import { PastDueChip, StatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { percentOf, subtractAmounts } from '@/lib/money';
import { denyPermission } from '@/lib/permissions';
import { ApiError, localizeServerCause, throwApiError } from '@/lib/server-messages';

import {
  BILLS_KEY,
  fetchBillsPage,
  PURCHASE_ORDERS_KEY,
  useBill,
  useBillStats,
  useCancelBill,
  usePagedBills,
  usePostBill,
} from './api';
import { billSteps } from './purchase-steppers';
import { PurchasesTabs } from './purchases-tabs';
import { MatchChip } from './status-chips';

const STATUSES: BillResponse['status'][] = ['draft', 'posted', 'cancelled'];
const TONES = { draft: 'draft', posted: 'posted', cancelled: 'cancelled' } as const;

type BillStats = ReturnType<typeof useBillStats>['data'] & object;

/**
 * PUR-03's list on the shared list-and-detail page (wave 3B). Only posted
 * bills reach payables (BR-PUR-03-10) and only they reach the money tiles.
 * The selected bill sits at Bill in the purchasing chain with its order
 * linked and the receipt leg read off `receivedTotal`, says how much is
 * paid and how many days to (or past) its due date, wears §7.5's computed
 * match verdict (BR-PUR-03-14) beside the status, and offers the acts: post
 * a draft, record a payment against a posted one (FIN-04, supplier + bill
 * carried), raise a debit note, open the match in the editor, print it,
 * cancel it (BR-PUR-03-11, reason required — GEN-72). BR-MD-09-09's Past Due
 * chip rides beside the status exactly as on the table.
 */
export function BillsListPage() {
  const { t, i18n } = useTranslation();
  const locale = documentLocale(i18n.language);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cancelAsk, setCancelAsk] = useState(false);
  const [params] = useSearchParams();
  const selectedId = params.get('selected') ?? '';
  const postBill = usePostBill(selectedId);
  const cancelBill = useCancelBill(selectedId);

  const act = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.status === 403) denyPermission('purchasing.approve');
      setError(localizeServerCause(t, thrown, 'doclist.actFailed'));
    } finally {
      setBusy(false);
    }
  };

  const statusLabel = (status: BillResponse['status']) => t(`common.status.${status}`);
  const chip = (bill: BillResponse) => (
    <span className="inline-flex gap-1">
      <StatusChip status={bill.status} tones={TONES} ns="common" />
      <PastDueChip level={bill.pastDue} />
    </span>
  );
  const exportColumns = [
    { header: t('bills.colNo'), value: (b: BillResponse) => b.code },
    { header: t('common.date'), value: (b: BillResponse) => b.invoiceDate, kind: 'date' as const },
    { header: t('bills.colDue'), value: (b: BillResponse) => b.dueDate, kind: 'date' as const },
    { header: t('po.colSupplier'), value: (b: BillResponse) => b.partnerName },
    { header: t('bills.colOrder'), value: (b: BillResponse) => b.purchaseOrderCode ?? '' },
    { header: t('bills.colMatch'), value: (b: BillResponse) => t(`bills.matchStatus.${b.matchStatus}`) },
    { header: t('sales.colStatus'), value: (b: BillResponse) => statusLabel(b.status) },
    { header: t('sales.exportColCurrency'), value: (b: BillResponse) => b.currency },
    { header: t('sales.colGrandTotal'), value: (b: BillResponse) => b.grandTotal, kind: 'number' as const },
    { header: t('sales.colBalanceDue'), value: (b: BillResponse) => b.balanceDue, kind: 'number' as const },
  ];
  const pdfRequest = (bill: BillResponse) => ({ kind: 'purchase_invoice' as const, locale, subjectId: bill.id });
  const toFilters = (filters: { status: BillResponse['status'] | ''; q: string; dateFrom: string; dateTo: string; partnerId: string }) => ({
    status: filters.status || undefined,
    q: filters.q || undefined,
    dateFrom: filters.dateFrom || undefined,
    dateTo: filters.dateTo || undefined,
    partnerId: filters.partnerId || undefined,
  });
  /** Wave 1C's "Due in N days / Overdue N days" from the server's `daysToDue` (the company's today). */
  const dueWords = (bill: BillResponse) =>
    bill.daysToDue === 0
      ? t('bills.dueToday')
      : bill.daysToDue > 0
        ? t('bills.dueIn', { count: bill.daysToDue })
        : t('bills.overdueBy', { count: -bill.daysToDue });

  const config: DocumentListConfig<BillResponse, BillResponse['status'], BillStats> = {
    titleKey: 'bills.title',
    tabs: <PurchasesTabs />,
    newAction: { to: '/purchases/bills/new', labelKey: 'common.new', permission: 'purchasing.create', reasonKey: 'perm.cannot.createBills' },
    statuses: STATUSES,
    statusLabel,
    search: { labelKey: 'bills.searchLabel', placeholderKey: 'bills.searchPlaceholder' },
    dateRange: true,
    partner: { kind: 'supplier' },
    useList: (filters, page, pageSize) => {
      const query = usePagedBills(toFilters(filters), page, pageSize);
      return { data: normalisePaged(query.data, 'bills'), isPending: query.isPending, isError: query.isError };
    },
    useById: (id) => useBill(id ?? undefined),
    kpis: {
      useStats: () => {
        const stats = useBillStats();
        return { data: stats.data, isPending: stats.isPending, isError: stats.isError };
      },
      tiles: [
        { key: 'payable', labelKey: 'bills.tilePayable', format: 'money', value: (s) => s.payableOutstanding, currency: (s) => s.currency },
        { key: 'overdueCount', labelKey: 'bills.tileOverdueCount', format: 'count', value: (s) => s.overdueCount, tone: 'warn' },
        { key: 'overdueValue', labelKey: 'bills.tileOverdueValue', format: 'money', value: (s) => s.overdueValue, currency: (s) => s.currency, tone: 'warn' },
        { key: 'dueSoon', labelKey: 'bills.tileDueSoon', format: 'count', value: (s) => s.dueIn7d },
        { key: 'variance', labelKey: 'bills.tileVariance', format: 'count', value: (s) => s.varianceCount, tone: 'warn' },
      ],
    },
    card: (bill) => ({
      code: bill.code,
      chip: chip(bill),
      title: bill.partnerName,
      meta: <bdi dir="ltr">{bill.invoiceDate}</bdi>,
      amount: { value: bill.grandTotal, currency: bill.currency },
    }),
    columns: { titleKey: 'po.colSupplier', metaKey: 'common.date', amountKey: 'sales.colGrandTotal', statusKey: 'sales.colStatus' },
    emptyKey: 'bills.empty',
    selectOneKey: 'bills.selectOne',
    notice: error ? (
      <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        {error}
      </p>
    ) : null,
    export: {
      filename: 'bills',
      columns: exportColumns,
      fetchPage: (filters, page, pageSize) => fetchBillsPage(toFilters(filters), page, pageSize),
    },
    bulkActions: (rows, clear) => (
      <>
        <BulkPostButton
          rows={rows}
          permission="purchasing.approve"
          reasonKey="perm.cannot.postBills"
          invalidate={[BILLS_KEY, PURCHASE_ORDERS_KEY]}
          clear={clear}
          post={async (row) => {
            const response = await api.purchaseInvoices.post({ params: { billId: row.id }, body: {} });
            if (response.status !== 200) throwApiError(response.status, response.body);
          }}
        />
        <BulkPdfButton requests={rows.map(pdfRequest)} />
        <BulkExportButton filename="bills" columns={exportColumns} rows={rows} />
      </>
    ),
    detail: {
      chip,
      links: (bill) =>
        bill.purchaseOrderCode
          ? [
              {
                key: 'order',
                to: `/purchases/orders?selected=${bill.purchaseOrderId}`,
                tone: 'muted',
                label: t('bills.fromOrder', { code: bill.purchaseOrderCode }),
              },
            ]
          : [],
      /* §7.5's verdict chip is a chip, not a sentence — it rides on the subline beside the due date. */
      subline: (bill) => (
        <span className="inline-flex flex-wrap items-center gap-2">
          <span>
            {bill.partnerName} · {t('bills.colDue')} <bdi dir="ltr">{bill.dueDate}</bdi>
          </span>
          <MatchChip status={bill.matchStatus} />
        </span>
      ),
      steps: { labelKey: 'po.stepper', of: billSteps },
      /* "% paid · days to due / overdue" — a draft owes nothing yet, a cancelled bill never will. */
      progress: (bill) => {
        if (bill.status !== 'posted') return null;
        const paid = percentOf(subtractAmounts(bill.grandTotal, bill.balanceDue), bill.grandTotal);
        return bill.fullyPaid
          ? t('bills.listProgressPaid', { paid })
          : t('bills.listProgress', { paid, due: dueWords(bill) });
      },
      actions: (bill) => (
        <>
          <Button asChild variant="outline">
            <Link to={`/purchases/bills/${bill.id}`}>
              <Pencil aria-hidden /> {bill.status === 'draft' ? t('common.edit') : t('doclist.open')}
            </Link>
          </Button>
          {bill.status === 'draft' ? (
            <Gated permission="purchasing.approve" reasonKey="perm.cannot.postBills">
              <Button onClick={() => void act(() => postBill.mutateAsync({}))} disabled={busy}>
                <CheckCheck aria-hidden /> {t('bills.actPost')}
              </Button>
            </Gated>
          ) : null}
          {bill.status === 'posted' && !bill.fullyPaid ? (
            <Gated permission="accounting.create" reasonKey="perm.cannot.createPayments">
              <Button asChild variant="outline">
                <Link to={`/accounting/payments/new?partnerId=${bill.partnerId}&billId=${bill.id}&currency=${bill.currency}`}>
                  <Banknote aria-hidden /> {t('bills.actRecordPayment')}
                </Link>
              </Button>
            </Gated>
          ) : null}
          {bill.status === 'posted' ? (
            <Gated permission="purchasing.create" reasonKey="perm.cannot.createPurchaseReturns">
              <Button asChild variant="outline">
                <Link to={`/purchases/returns/new?billId=${bill.id}`}>
                  <RotateCcw aria-hidden /> {t('bills.actDebitNote')}
                </Link>
              </Button>
            </Gated>
          ) : null}
          {bill.purchaseOrderId ? (
            <Button asChild variant="outline">
              <Link to={`/purchases/bills/${bill.id}`}>
                <Scale aria-hidden /> {t('bills.actViewMatch')}
              </Link>
            </Button>
          ) : null}
          <DocumentPdfButton request={pdfRequest(bill)} />
          {bill.status === 'posted' ? (
            <Gated permission="purchasing.approve" reasonKey="perm.cannot.cancelBills">
              <Button variant="destructive" onClick={() => setCancelAsk(true)} disabled={busy}>
                <XCircle aria-hidden /> {t('bills.actCancel')}
              </Button>
            </Gated>
          ) : null}
          {bill.status === 'posted' ? (
            <ReasonBox
              open={cancelAsk}
              title={t('bills.listCancelTitle')}
              prompt={t('bills.listCancelBody')}
              reasonLabel={t('gen72.cancelReasonLabel')}
              confirmLabel={t('gen72.confirmCancel')}
              busy={busy}
              onConfirm={(reason) => {
                setCancelAsk(false);
                void act(() => cancelBill.mutateAsync(reason));
              }}
              onCancel={() => setCancelAsk(false)}
            />
          ) : null}
        </>
      ),
      lines: {
        columns: [
          { key: 'item', labelKey: 'quotes.colItem' },
          { key: 'qty', labelKey: 'sales.colQty', align: 'end', className: 'w-24' },
          { key: 'unitPrice', labelKey: 'sales.colUnitPrice', align: 'end', className: 'w-32' },
          { key: 'total', labelKey: 'sales.colTotal', align: 'end', className: 'w-32' },
        ],
        rows: (bill) =>
          bill.lines.map((line) => ({
            key: String(line.lineNo),
            cells: [
              <span key="d" className="font-medium">{line.description}</span>,
              <bdi key="q" dir="ltr" className="font-mono">{line.qty.replace(/\.?0+$/, '')}</bdi>,
              <Amount key="u" value={line.unitPrice} currency={bill.currency} />,
              <Amount key="t" value={line.lineTotal} currency={bill.currency} />,
            ],
          })),
      },
      totals: (bill) => ({
        labelKey: 'orders.netTaxTotal',
        amounts: [
          { value: bill.subtotal, currency: bill.currency },
          { value: bill.taxAmount, currency: bill.currency },
          { value: bill.grandTotal, currency: bill.currency, emphasis: true },
        ],
      }),
    },
  };

  return <DocumentListPage config={config} />;
}
